import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { getProfile } from 'actions/profile';
import AuthService from 'services/auth';
import { getPlayableGames } from 'constants/GameListAdapter';
import placeHolderCard from 'images/gameStorePreviews/placeholder-card.svg';
import styles from '../ProfileStyles.module.scss';

const AccountOverview = ({ onSectionChange }) => {
    const dispatch = useDispatch();
    const { profile, profileLoading } = useSelector(state => state.profile);
    const { user } = useSelector(state => state.auth);
    const [userGames, setUserGames] = useState([]);
    const [gamesLoading, setGamesLoading] = useState(true);

    useEffect(() => {
        dispatch(getProfile());
    }, [dispatch]);

    useEffect(() => {
        const fetchGames = async () => {
            try {
                setGamesLoading(true);
                const result = await AuthService.getGames(user?.email);
                if (result.games) {
                    setUserGames(result.games);
                }
            } catch (err) {
                // Silently fail - games section will just show empty
            } finally {
                setGamesLoading(false);
            }
        };

        if (user?.email) {
            fetchGames();
        }
    }, [user?.email]);

    // Match user games to GameList entries and remove duplicates
    // Only show enabled individual games, not packs, addons, or disabled games
    const getOwnedGamesList = () => {
        const ownedGameIds = new Set();
        const ownedGameList = [];

        getPlayableGames().forEach((game) => {
            // Only include enabled games, not disabled ones
            if (game.status === 'disabled') return;

            // Check if user owns this game and hasn't been added yet
            if (userGames.findIndex((ug) => ug.code === game.gameId) > -1 && !ownedGameIds.has(game.gameId)) {
                ownedGameIds.add(game.gameId);
                ownedGameList.push(game);
            }
        });

        return ownedGameList;
    };

    if (profileLoading) {
        return (
            <div className={styles.loading}>
                <div className={styles.loadingSpinner}></div>
            </div>
        );
    }

    const ownedGames = getOwnedGamesList();
    const previewGames = ownedGames.slice(0, 4);

    return (
        <div>
            <h2 className={styles.sectionTitle}>Account Overview</h2>
            <p className={styles.sectionDescription}>
                View your account information and activity summary.
            </p>

            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <span className={styles.statValue}>{profile?.totalGamesOwned || 0}</span>
                    <span className={styles.statLabel}>Games Owned</span>
                </div>
                <div className={styles.statCard}>
                    <span className={styles.statValue}>{profile?.totalVouchersRedeemed || 0}</span>
                    <span className={styles.statLabel}>Codes Redeemed</span>
                </div>
            </div>

            <div className={styles.card}>
                <h3 className={styles.cardTitle}>Account Details</h3>

                <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Email Address</span>
                    <span className={styles.infoValue}>{user?.email || profile?.email || 'N/A'}</span>
                </div>

                {profile?.hasRoomPin && (
                    <div className={styles.infoRow}>
                        <span className={styles.infoLabel}>Room PIN</span>
                        <span className={styles.infoValue}>Set</span>
                    </div>
                )}
            </div>

            {!gamesLoading && previewGames.length > 0 && (
                <div className={styles.card}>
                    <h3 className={styles.cardTitle}>My Games</h3>
                    <div className={styles.ownedGamesGrid}>
                        {previewGames.map((game) => (
                            game.meta?.tinyCard ? (
                                <img
                                    key={game.gameId}
                                    src={game.meta.tinyCard}
                                    alt={game.name}
                                    className={styles.ownedGameImage}
                                />
                            ) : (
                                <div key={game.gameId} className={styles.ownedGamePlaceholder}>
                                    <span className={styles.placeholderName}>{game.name}</span>
                                    <img src={placeHolderCard} alt={game.name} className={styles.ownedGameImage} />
                                </div>
                            )
                        ))}
                    </div>
                    {ownedGames.length > 4 && (
                        <div className={styles.viewButtonWrapper}>
                            <button
                                onClick={() => onSectionChange('games')}
                                className={styles.viewButton}
                            >
                                View All Games ({ownedGames.length})
                            </button>
                        </div>
                    )}
                    {ownedGames.length <= 4 && (
                        <div className={styles.viewButtonWrapper}>
                            <button
                                onClick={() => onSectionChange('games')}
                                className={styles.viewButton}
                            >
                                View All Games
                            </button>
                        </div>
                    )}
                </div>
            )}

            {!gamesLoading && previewGames.length === 0 && (
                <div className={styles.card}>
                    <div className={styles.emptyState}>
                        <div className={styles.emptyIcon}>🎮</div>
                        <h3 className={styles.emptyTitle}>No Games Yet</h3>
                        <p className={styles.emptyText}>
                            You don't have any games in your account yet. If you have an activation code,
                            head to the redeem code page to add games to your account. If you've already redeemed codes, make sure
                            you're logged into the correct account.
                        </p>
                        <Link to="/redeem-code" className={`${styles.button} ${styles.small}`}>
                            Redeem Code
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountOverview;
